import React, { useState } from 'react';

interface Player {
  id: string;
  name: string;
  role?: string;
  battingStyle?: string;
  bowlingStyle?: string;
  age?: number;
  country?: string;
}

interface TeamSquad {
  teamName: string;
  shortname?: string;
  players: Player[];
}

interface ManualSquadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (squad: TeamSquad[]) => void;
  availableTeams: Array<{ name: string; shortname: string }>;
}

const ManualSquadModal: React.FC<ManualSquadModalProps> = ({ isOpen, onClose, onAdd, availableTeams }) => {
  const [squad, setSquad] = useState<TeamSquad[]>([{
    teamName: '',
    shortname: '',
    players: [{ id: '', name: '', role: '', battingStyle: '', bowlingStyle: '' }]
  }]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const addTeam = () => {
    setSquad([...squad, {
      teamName: '',
      shortname: '',
      players: [{ id: '', name: '', role: '', battingStyle: '', bowlingStyle: '' }]
    }]);
  };

  const removeTeam = (teamIndex: number) => {
    if (squad.length > 1) {
      setSquad(squad.filter((_, i) => i !== teamIndex));
    }
  };

  const updateTeam = (teamIndex: number, field: keyof TeamSquad, value: string) => {
    const updatedSquad = [...squad];
    updatedSquad[teamIndex] = { ...updatedSquad[teamIndex], [field]: value };
    
    // Auto-populate shortname when team is selected
    if (field === 'teamName') {
      const selectedTeam = availableTeams.find(team => team.name === value);
      if (selectedTeam) {
        updatedSquad[teamIndex].shortname = selectedTeam.shortname;
      }
    }
    
    setSquad(updatedSquad);
  };

  const addPlayer = (teamIndex: number) => {
    const updatedSquad = [...squad];
    updatedSquad[teamIndex].players.push({
      id: '',
      name: '',
      role: '',
      battingStyle: '',
      bowlingStyle: ''
    });
    setSquad(updatedSquad);
  };

  const removePlayer = (teamIndex: number, playerIndex: number) => {
    const updatedSquad = [...squad];
    if (updatedSquad[teamIndex].players.length > 1) {
      updatedSquad[teamIndex].players.splice(playerIndex, 1);
      setSquad(updatedSquad);
    }
  };

  const updatePlayer = (teamIndex: number, playerIndex: number, field: keyof Player, value: string) => {
    const updatedSquad = [...squad];
    updatedSquad[teamIndex].players[playerIndex] = {
      ...updatedSquad[teamIndex].players[playerIndex],
      [field]: value
    };
    setSquad(updatedSquad);
  };

  const handleSubmit = async () => {
    // Validate required fields
    const validSquad = squad.filter(team => 
      team.teamName.trim() && team.players.some(player => player.name.trim() && player.id.trim())
    );
    
    if (validSquad.length === 0) {
      alert('Please add at least one team with players (name and ID required)');
      return;
    }

    // Check for duplicate teams
    const teamNames = validSquad.map(team => team.teamName);
    const uniqueTeamNames = new Set(teamNames);
    if (teamNames.length !== uniqueTeamNames.size) {
      alert('Duplicate teams detected. Please ensure each team is unique.');
      return;
    }

    // Filter out empty players
    const processedSquad = validSquad.map(team => ({
      ...team,
      players: team.players.filter(player => player.name.trim() && player.id.trim())
    }));

    setIsSubmitting(true);
    try {
      await onAdd(processedSquad);
      setSquad([{
        teamName: '',
        shortname: '',
        players: [{ id: '', name: '', role: '', battingStyle: '', bowlingStyle: '' }]
      }]);
      onClose();
    } catch (error) {
      console.error('Error adding squad:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  // Batting and bowling style options (from CricAPI)
  const BATTING_STYLES = [
    '',
    'Right-hand bat',
    'Left-hand bat',
    'Ambidextrous'
  ];
  const BOWLING_STYLES = [
    '',
    'Right-arm fast',
    'Right-arm medium',
    'Right-arm offbreak',
    'Right-arm legbreak',
    'Left-arm fast',
    'Left-arm medium',
    'Left-arm orthodox',
    'Left-arm chinaman',
    'Left-arm offbreak',
    'Left-arm legbreak'
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-6xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Add Squad Manually</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        {availableTeams.length === 0 && (
          <div className="mb-4 p-3 bg-yellow-100 border border-yellow-400 rounded text-yellow-700">
            ⚠️ No teams available. Please add teams first in the Teams tab.
          </div>
        )}

        <div className="space-y-6">
          {squad.map((team, teamIndex) => (
            <div key={teamIndex} className="border rounded-lg p-4 bg-gray-50">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-lg">Team {teamIndex + 1}</h3>
                {squad.length > 1 && (
                  <button
                    onClick={() => removeTeam(teamIndex)}
                    className="text-red-500 hover:text-red-700 text-sm"
                  >
                    Remove Team
                  </button>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Team Name *
                  </label>
                  <select
                    value={team.teamName}
                    onChange={(e) => updateTeam(teamIndex, 'teamName', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select Team</option>
                    {availableTeams.map((availableTeam) => (
                      <option key={availableTeam.name} value={availableTeam.name}>
                        {availableTeam.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Short Name (Auto-populated)
                  </label>
                  <input
                    type="text"
                    value={team.shortname || ''}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600"
                    placeholder="Will be auto-populated"
                  />
                </div>
              </div>

              <div className="mb-3">
                <h4 className="font-medium text-gray-700 mb-2">Players</h4>
                <button
                  onClick={() => addPlayer(teamIndex)}
                  className="bg-green-500 text-white px-3 py-1 rounded-md hover:bg-green-600 text-sm"
                >
                  + Add Player
                </button>
              </div>

              <div className="space-y-3">
                {team.players.map((player, playerIndex) => (
                  <div key={playerIndex} className="border rounded p-3 bg-white">
                    <div className="flex justify-between items-center mb-2">
                      <h5 className="font-medium">Player {playerIndex + 1}</h5>
                      {team.players.length > 1 && (
                        <button
                          onClick={() => removePlayer(teamIndex, playerIndex)}
                          className="text-red-500 hover:text-red-700 text-sm"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Player ID *
                        </label>
                        <input
                          type="text"
                          value={player.id}
                          onChange={(e) => updatePlayer(teamIndex, playerIndex, 'id', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g., player_123"
                          required
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Player Name *
                        </label>
                        <input
                          type="text"
                          value={player.name}
                          onChange={(e) => updatePlayer(teamIndex, playerIndex, 'name', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g., Rohit Sharma"
                          required
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Role
                        </label>
                        <select
                          value={player.role || ''}
                          onChange={(e) => updatePlayer(teamIndex, playerIndex, 'role', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Select Role</option>
                          <option value="batsman">Batsman</option>
                          <option value="bowler">Bowler</option>
                          <option value="all-rounder">All-Rounder</option>
                          <option value="wicket-keeper">Wicket-Keeper</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Batting Style
                        </label>
                        <select
                          value={player.battingStyle || ''}
                          onChange={(e) => updatePlayer(teamIndex, playerIndex, 'battingStyle', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          {BATTING_STYLES.map(style => (
                            <option key={style} value={style}>{style || 'Select Batting Style'}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Bowling Style
                        </label>
                        <select
                          value={player.bowlingStyle || ''}
                          onChange={(e) => updatePlayer(teamIndex, playerIndex, 'bowlingStyle', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          {BOWLING_STYLES.map(style => (
                            <option key={style} value={style}>{style || 'Select Bowling Style'}</option>
                          ))}
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Country
                        </label>
                        <input
                          type="text"
                          value={player.country || ''}
                          onChange={(e) => updatePlayer(teamIndex, playerIndex, 'country', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g., India"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4">
          <button
            onClick={addTeam}
            className="bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600 mr-2"
            disabled={availableTeams.length === 0}
          >
            + Add Another Team
          </button>
        </div>

        <div className="mt-6 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || availableTeams.length === 0}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50"
          >
            {isSubmitting ? 'Adding...' : 'Add Squad'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ManualSquadModal; 